import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import useDocumentTitle from '../hooks/useDocumentTitle';
import UsersEngagementLineChart from '../components/admin/UsersEngagementLineChart';
import PremiumSummaryCard, {
  MonetizationSummaryMetrics as MonetizationSummaryMetricsProp
} from '../components/admin/PremiumSummaryCard';
import Sparkline, { SparklinePoint } from '../components/admin/Sparkline';

interface AdminStats {
  users: {
    total: number;
    activeToday: number;
    activeThisWeek: number;
  };
  questions: {
    total: number;
  };
  quizResults: {
    total: number;
  };
}

interface AdminStatsResponse {
  success: boolean;
  data?: AdminStats;
  message?: string;
}

interface UsersSummaryMetrics {
  totalUsers: number;
  newUsersLast7Days: number;
  newUsersLast30Days: number;
  activeToday: number;
  activeLast7Days: number;
  activeLast30Days: number;
  totalPremiumUsers: number;
  totalAdminUsers: number;
}

interface EngagementSummaryMetrics {
  totalQuizzes: number;
  quizzesLast7Days: number;
  quizzesLast30Days: number;
  avgScoreAllTime: number;
  avgScoreLast30Days: number;
  avgCompletionTimeAllTime: number;
  avgCompletionTimeLast30Days: number;
}

interface ContentSummaryMetrics {
  totalQuestions: number;
  totalSubtopics: number;
  averageQuestionsPerSubtopic: number;
  subtopicsBelowTarget: number;
  pendingCandidateQuestions: number;
}

interface MonetizationSummaryMetrics {
  totalPremiumUsers: number;
  premiumUsersLast30Days: number;
  upgradeClicksLast7Days: number;
  upgradeClicksLast30Days: number;
}

interface SecuritySummaryMetrics {
  failedLoginsLast24Hours: number;
  failedLoginsLast7Days: number;
  lockedAccounts: number;
  bannedUsers: number;
}

interface TrendResponse<T> {
  success: boolean;
  data: T;
}

interface JobsSummaryMetrics {
  jobs: Array<{
    name: string;
    enabled: boolean;
    schedule: string;
    lastRun: string | null;
    status: 'ok' | 'warning' | 'error' | 'unknown';
  }>;
}

interface QuizInteractivitySummaryMetrics {
  loggedInCompletions: number;
  guestCompletions: number;
  totalCompletions: number;
  percentLoggedIn: number;
  loggedInLast7Days: number;
  guestLast7Days: number;
  loggedInLast30Days: number;
  guestLast30Days: number;
}

interface QuizInteractivityTrendPoint {
  date: string;
  loggedInCompletions: number;
  guestCompletions: number;
  totalCompletions: number;
  percentLoggedIn: number;
}

interface PerformanceSummaryMetrics {
  database: {
    totalSize: number;
    totalSizeFormatted: string;
    tableSizes: Array<{
      tableName: string;
      size: number;
      sizeFormatted: string;
      rowCount: number;
    }>;
  };
  connectionPool: {
    totalConnections: number;
    idleConnections: number;
    activeConnections: number;
    waitingConnections: number;
  };
  errorRates: {
    errorsLast24Hours: number;
    errorsLast7Days: number;
    errorsLast30Days: number;
    errorRateLast24Hours: number;
  };
  queryPerformance: {
    slowQueriesLast24Hours: number;
    avgQueryTime: number;
  };
}

const AdminDashboard: React.FC = () => {
  useDocumentTitle('Cognidaily | Admin Dashboard');

  // Date range state - default to last 30 days
  const getToday = () => new Date().toISOString().split('T')[0];
  const getThirtyDaysAgo = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };
  
  const [dateFrom, setDateFrom] = useState<string>(getThirtyDaysAgo());
  const [dateTo, setDateTo] = useState<string>(getToday());
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const [includeBots, setIncludeBots] = useState(false);

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [usersSummary, setUsersSummary] = useState<UsersSummaryMetrics | null>(
    null
  );
  const [engagementSummary, setEngagementSummary] =
    useState<EngagementSummaryMetrics | null>(null);
  const [contentSummary, setContentSummary] =
    useState<ContentSummaryMetrics | null>(null);
  const [monetizationSummary, setMonetizationSummary] =
    useState<MonetizationSummaryMetrics | null>(null);
  const [securitySummary, setSecuritySummary] =
    useState<SecuritySummaryMetrics | null>(null);
  const [jobsSummary, setJobsSummary] = useState<JobsSummaryMetrics | null>(
    null
  );
  const [performanceSummary, setPerformanceSummary] = useState<PerformanceSummaryMetrics | null>(
    null
  );
  const [failedLoginTrend, setFailedLoginTrend] = useState<SparklinePoint[]>([]);
  const [lockedTrend, setLockedTrend] = useState<SparklinePoint[]>([]);
  const [pendingCandidatesTrend, setPendingCandidatesTrend] = useState<SparklinePoint[]>([]);
  const [genJobTrend, setGenJobTrend] = useState<Array<{ date: string; succeeded: number; failed: number }>>([]);
  const [quizInteractivitySummary, setQuizInteractivitySummary] = useState<QuizInteractivitySummaryMetrics | null>(null);
  const [quizInteractivityTrend, setQuizInteractivityTrend] = useState<QuizInteractivityTrendPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const loadingRef = useRef(false);
  const { user, loading: authLoading } = useAuth();

  // Update date range when days preset changes
  const handleDaysChange = (newDays: 7 | 30 | 90) => {
    setDays(newDays);
    const today = new Date();
    const newDateFrom = new Date(today);
    newDateFrom.setDate(today.getDate() - newDays);
    setDateFrom(newDateFrom.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  };

  const clampDays = (value: number): 7 | 30 | 90 => {
    // Keep it simple: clamp to supported presets
    if (value <= 7) return 7;
    if (value <= 30) return 30;
    return 90;
  };

  const handleApplyCustomRange = () => {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return;
    const diffMs = Math.abs(to.getTime() - from.getTime());
    const diffDays = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    setDays(clampDays(diffDays));
  };

  const loadStats = async (isRefresh = false, currentDays = days, currentIncludeBots = includeBots) => {
    // Prevent concurrent requests
    if (loadingRef.current) {
      return;
    }
    
    // Don't make API calls if user is not authenticated
    if (!user || authLoading) {
      return;
    }
    
    try {
      loadingRef.current = true;
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

        const results = await Promise.allSettled([
          axios.get<AdminStatsResponse>('/api/admin/stats', {
            params: { includeBots: currentIncludeBots }
          }),
          axios.get<{
            success: boolean;
            data?: UsersSummaryMetrics;
            message?: string;
          }>('/api/admin/metrics/users/summary', {
            params: { includeBots: currentIncludeBots }
          }),
          axios.get<{ success: boolean; data?: EngagementSummaryMetrics }>(
            '/api/admin/metrics/engagement/summary',
            { params: { includeBots: currentIncludeBots } }
          ),
          axios.get<{ success: boolean; data?: ContentSummaryMetrics }>(
            '/api/admin/metrics/content/summary'
          ),
          axios.get<{ success: boolean; data?: MonetizationSummaryMetrics }>(
            '/api/admin/metrics/monetization/summary'
          ),
          axios.get<{ success: boolean; data?: SecuritySummaryMetrics }>(
            '/api/admin/metrics/security/summary'
          ),
          axios.get<{ success: boolean; data?: JobsSummaryMetrics }>(
            '/api/admin/jobs/summary'
          ),
          axios.get<{ success: boolean; data?: PerformanceSummaryMetrics }>(
            '/api/admin/metrics/performance/summary'
          ),
          axios.get<TrendResponse<SparklinePoint[]>>(
            '/api/admin/metrics/security/failed-logins-trend',
            { params: { days: currentDays, includeBots: currentIncludeBots } }
          ),
          axios.get<TrendResponse<SparklinePoint[]>>(
            '/api/admin/metrics/security/locked-accounts-trend',
            { params: { days: currentDays, includeBots: currentIncludeBots } }
          ),
          axios.get<TrendResponse<SparklinePoint[]>>(
            '/api/admin/metrics/content/pending-candidates-trend',
            { params: { days: currentDays } }
          ),
          axios.get<TrendResponse<Array<{ date: string; succeeded: number; failed: number }>>>(
            '/api/admin/metrics/jobs/question-generation-outcomes-trend',
            { params: { days: currentDays } }
          ),
          axios.get<{ success: boolean; data?: QuizInteractivitySummaryMetrics }>(
            '/api/admin/metrics/quiz-interactivity/summary',
            { params: { days: currentDays, includeBots: currentIncludeBots } }
          ),
          axios.get<TrendResponse<QuizInteractivityTrendPoint[]>>(
            '/api/admin/metrics/quiz-interactivity/trends',
            { params: { days: currentDays, includeBots: currentIncludeBots } }
          )
        ]);

        // Extract responses from Promise.allSettled results, handling both fulfilled and rejected promises
        const extractResponse = (result: PromiseSettledResult<any>) => {
          if (result.status === 'fulfilled') {
            return result.value;
          } else {
            // Rejected promise - create a mock response object
            return {
              data: { 
                success: false, 
                message: result.reason?.response?.data?.message || 'Request failed' 
              },
              status: result.reason?.response?.status || 500
            };
          }
        };

        const [
          statsRes,
          usersRes,
          engagementRes,
          contentRes,
          monetizationRes,
          securityRes,
          jobsRes,
          performanceRes,
          failedLoginTrendRes,
          lockedTrendRes,
          pendingCandidatesTrendRes,
          genJobTrendRes,
          quizInteractivitySummaryRes,
          quizInteractivityTrendRes
        ] = results.map(extractResponse);

        // Handle responses - check for success and data
        if (statsRes.data?.success && statsRes.data?.data) {
          setStats(statsRes.data.data);
        }
        if (usersRes.data?.success && usersRes.data?.data) {
          setUsersSummary(usersRes.data.data);
        }
        if (engagementRes.data?.success && engagementRes.data?.data) {
          setEngagementSummary(engagementRes.data.data);
        }
        if (contentRes.data?.success && contentRes.data?.data) {
          setContentSummary(contentRes.data.data);
        }
        if (monetizationRes.data?.success && monetizationRes.data?.data) {
          setMonetizationSummary(monetizationRes.data.data);
        }
        if (securityRes.data?.success && securityRes.data?.data) {
          setSecuritySummary(securityRes.data.data);
        }
        if (jobsRes.data?.success && jobsRes.data?.data) {
          setJobsSummary(jobsRes.data.data);
        }
        if (performanceRes.data?.success && performanceRes.data?.data) {
          setPerformanceSummary(performanceRes.data.data);
        }
        if (failedLoginTrendRes.data?.success && failedLoginTrendRes.data?.data) {
          setFailedLoginTrend(failedLoginTrendRes.data.data || []);
        }
        if (lockedTrendRes.data?.success && lockedTrendRes.data?.data) {
          setLockedTrend(lockedTrendRes.data.data || []);
        }
        if (pendingCandidatesTrendRes.data?.success && pendingCandidatesTrendRes.data?.data) {
          setPendingCandidatesTrend(pendingCandidatesTrendRes.data.data || []);
        }
        if (genJobTrendRes.data?.success && genJobTrendRes.data?.data) {
          setGenJobTrend(genJobTrendRes.data.data || []);
        }
        if (quizInteractivitySummaryRes.data?.success && quizInteractivitySummaryRes.data?.data) {
          setQuizInteractivitySummary(quizInteractivitySummaryRes.data.data);
        }
        if (quizInteractivityTrendRes.data?.success && quizInteractivityTrendRes.data?.data) {
          setQuizInteractivityTrend(quizInteractivityTrendRes.data.data || []);
        }

        // Only set error if critical endpoints failed (not just 401s)
        const hasCriticalError = !statsRes.data?.success && !usersRes.data?.success;
        const isUnauthorized = statsRes.status === 401 || usersRes.status === 401 ||
                               statsRes.data?.message?.toLowerCase().includes('unauthorized') || 
                               usersRes.data?.message?.toLowerCase().includes('unauthorized');
        
        if (hasCriticalError && !isUnauthorized) {
          setError(
            statsRes.data?.message ||
              usersRes.data?.message ||
              'Failed to load admin statistics.'
          );
        } else if (!hasCriticalError) {
          setLastUpdated(new Date());
        }
      } catch (err: any) {
        // Only log non-401 errors to reduce console noise
        const isUnauthorized = err?.response?.status === 401;
        if (!isUnauthorized) {
          console.error('[AdminDashboard] Failed to load stats', err);
        }
        // Only set error if it's not a 401 (unauthorized errors are expected if auth isn't ready)
        if (!isUnauthorized) {
          setError(
            err?.response?.data?.message ||
              'Failed to load admin statistics from server.'
          );
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
        loadingRef.current = false;
      }
    };

  useEffect(() => {
    // Only load stats when user is authenticated and not loading
    if (user && !authLoading) {
      loadStats(false, days, includeBots);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeBots, days, user, authLoading]);

  const handleRefresh = () => {
    loadStats(true, days, includeBots);
  };

  const totalUsers =
    usersSummary?.totalUsers ?? stats?.users.total ?? 0;
  const activeToday =
    usersSummary?.activeToday ?? stats?.users.activeToday ?? 0;
  const activeThisWeek =
    usersSummary?.activeLast7Days ?? stats?.users.activeThisWeek ?? 0;
  const totalPremiumUsers = usersSummary?.totalPremiumUsers ?? 0;

  const totalQuestions =
    contentSummary?.totalQuestions ?? stats?.questions.total ?? 0;
  const totalQuizzes =
    engagementSummary?.totalQuizzes ?? stats?.quizResults.total ?? 0;

  return (
    <div className="admin-page">
      <SEOHead
        title="Admin | Dashboard"
        description="Overview of key Cognidaily metrics and quick links to admin tools."
      />
      <header className="admin-page__header">
        <h1 className="admin-page__title">Dashboard</h1>
        <p className="admin-page__subtitle">Overview of key metrics and quick links.</p>
      </header>

      <div className="admin-page__filters">
        <label className="admin-checkbox">
          <input
            type="checkbox"
            checked={includeBots}
            onChange={(e) => setIncludeBots(e.target.checked)}
            aria-label="Include bot activity in statistics"
          />
          Include bots
        </label>
        <div className="admin-page__presets">
          {([7, 30, 90] as const).map((d) => (
            <button
              key={d}
              type="button"
              className={`btn btn-ghost btn-xs${days === d ? ' btn-ghost--active' : ''}`}
              onClick={() => handleDaysChange(d)}
            >
              {d} days
            </button>
          ))}
        </div>
        <div className="admin-page__date-inputs">
          <label>
            From:
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="Start date"
            />
          </label>
          <label>
            To:
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              max={getToday()}
              aria-label="End date"
            />
          </label>
          <button type="button" className="btn btn-secondary btn-xs" onClick={handleApplyCustomRange}>
            Apply
          </button>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={handleRefresh}
          disabled={refreshing || loading}
          aria-label="Refresh"
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        {lastUpdated && (
          <span className="admin-page__last-updated" role="status">
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>

      {loading && !stats ? (
        <div className="admin-loading" role="status">
          <div className="spinner" aria-hidden="true" />
          <p>Loading…</p>
        </div>
      ) : error && !stats ? (
        <div className="admin-error" role="alert">
          <strong>Error:</strong> {error}
          <button type="button" className="btn btn-secondary btn-xs" onClick={() => loadStats(false, days, includeBots)}>
            Retry
          </button>
        </div>
      ) : stats ? (
        <>
          {typeof contentSummary?.pendingCandidateQuestions === 'number' &&
              contentSummary.pendingCandidateQuestions > 0 && (
                <div className="admin-callout">
                  <div className="admin-callout__title">Candidate review queue</div>
                  <div className="admin-callout__body">
                    <strong>{contentSummary.pendingCandidateQuestions}</strong> pending question
                    {contentSummary.pendingCandidateQuestions === 1 ? '' : 's'}.
                  </div>
                  <div className="admin-callout__actions">
                    <Link to="/admin/question-review" className="btn btn-primary btn-xs">
                      Review now
                    </Link>
                  </div>
                </div>
              )}

          <section className="admin-grid" aria-label="Key metrics">
            <article className="admin-card" role="region">
              <div className="admin-card__label">Users</div>
              <div className="admin-card__value">{totalUsers}</div>
              <div className="admin-card__meta">
                {activeToday} today • {activeThisWeek} last 7 days
              </div>
            </article>
            <article className="admin-card" role="region">
              <div className="admin-card__label">Questions</div>
              <div className="admin-card__value">{totalQuestions}</div>
              <div className="admin-card__meta">Total in pool</div>
            </article>
            <article className="admin-card" role="region">
              <div className="admin-card__label">Quizzes</div>
              <div className="admin-card__value">{totalQuizzes}</div>
              <div className="admin-card__meta">All-time completed</div>
            </article>
            {monetizationSummary && (
              <article className="admin-card" role="region">
                <div className="admin-card__label">Premium</div>
                <div className="admin-card__value">{totalPremiumUsers}</div>
                <div className="admin-card__meta">
                  {monetizationSummary.upgradeClicksLast7Days} upgrade clicks (7d)
                </div>
              </article>
            )}
          </section>

          <section className="admin-section" aria-label="Users">
            <h2 className="admin-section__title">Users & engagement</h2>
            <p className="admin-section__subtitle">Active users and premium funnel.</p>
            <div className="admin-section__grid">
              <article className="admin-usage-card" role="region">
                <h3>Active users vs quizzes</h3>
                <UsersEngagementLineChart days={days} includeBots={includeBots} />
              </article>
              <article className="admin-usage-card" role="region">
                <PremiumSummaryCard
                  summary={(monetizationSummary as MonetizationSummaryMetricsProp) || null}
                  days={days}
                  includeBots={includeBots}
                />
              </article>
            </div>
          </section>

          {quizInteractivitySummary && (
            <section className="admin-section" aria-label="Quiz interactivity">
              <h2 className="admin-section__title">Quiz interactivity</h2>
              <p className="admin-section__subtitle">Logged-in vs guest quiz completions.</p>
              <div className="admin-section__grid">
                <article className="admin-usage-card" role="region">
                  <h3>Summary</h3>
                  <div className="admin-usage-row">
                    <span className="admin-usage-label">Total completions ({days}d)</span>
                    <span className="admin-usage-value">{quizInteractivitySummary.totalCompletions}</span>
                  </div>
                  <div className="admin-usage-row">
                    <span className="admin-usage-label">Logged in</span>
                    <span className="admin-usage-value">{quizInteractivitySummary.loggedInCompletions}</span>
                  </div>
                  <div className="admin-usage-row">
                    <span className="admin-usage-label">Guest</span>
                    <span className="admin-usage-value">{quizInteractivitySummary.guestCompletions}</span>
                  </div>
                  <div className="admin-usage-row">
                    <span className="admin-usage-label">% logged in</span>
                    <span className="admin-usage-value">{quizInteractivitySummary.percentLoggedIn}%</span>
                  </div>
                  <div className="admin-usage-row" style={{ marginTop: 'var(--space-2)' }}>
                    <span className="admin-usage-label">Last 7 days (logged in / guest)</span>
                    <span className="admin-usage-value">
                      {quizInteractivitySummary.loggedInLast7Days} / {quizInteractivitySummary.guestLast7Days}
                    </span>
                  </div>
                </article>
                <article className="admin-usage-card" role="region">
                  <h3>Daily trend (% logged in)</h3>
                  {quizInteractivityTrend.length > 0 ? (
                    <Sparkline
                      data={quizInteractivityTrend.map((p) => ({ date: p.date, count: p.percentLoggedIn }))}
                      stroke="#3182ce"
                    />
                  ) : (
                    <p className="admin-usage-meta">No data for selected period</p>
                  )}
                </article>
              </div>
            </section>
          )}

          {securitySummary && jobsSummary && (
            <section className="admin-section" aria-label="System">
              <h2 className="admin-section__title">Security & jobs</h2>
              <p className="admin-section__subtitle">Security signals and background jobs.</p>
              <div className="admin-section__grid">
                <article className="admin-usage-card" role="region">
                  <h3>Security</h3>
                  <div className="admin-usage-row">
                    <span className="admin-usage-label">Failed logins (24h)</span>
                    <span className="admin-usage-value">{securitySummary.failedLoginsLast24Hours}</span>
                  </div>
                  <div className="admin-usage-row">
                    <span className="admin-usage-label">Failed logins (7d)</span>
                    <span className="admin-usage-value">{securitySummary.failedLoginsLast7Days}</span>
                  </div>
                  <div className="admin-usage-row">
                    <span className="admin-usage-label">Locked</span>
                    <span className="admin-usage-value">{securitySummary.lockedAccounts}</span>
                  </div>
                  <div className="admin-usage-row">
                    <span className="admin-usage-label">Banned</span>
                    <span className="admin-usage-value">{securitySummary.bannedUsers}</span>
                  </div>
                  {failedLoginTrend.length > 0 && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <div className="admin-card__label">Failed login trend</div>
                      <Sparkline data={failedLoginTrend} stroke="#c53030" />
                    </div>
                  )}
                  {lockedTrend.length > 0 && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <div className="admin-card__label">Locked trend</div>
                      <Sparkline data={lockedTrend} stroke="#b7791f" />
                    </div>
                  )}
                </article>
                <article className="admin-usage-card" role="region">
                  <h3>Jobs</h3>
                  {jobsSummary.jobs.slice(0, 4).map((job) => (
                    <div key={job.name} className="admin-usage-row">
                      <span className="admin-usage-label">{job.name}</span>
                      <span className="admin-usage-value">{job.enabled ? 'On' : 'Off'}</span>
                    </div>
                  ))}
                  {pendingCandidatesTrend.length > 0 && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <div className="admin-card__label">Pending candidates</div>
                      <Sparkline data={pendingCandidatesTrend} stroke="#6366f1" />
                    </div>
                  )}
                  {genJobTrend.length > 0 && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <div className="admin-card__label">Gen job outcomes</div>
                      <Sparkline
                        data={genJobTrend.map((p) => ({ date: p.date, count: p.succeeded }))}
                        stroke="#2f855a"
                      />
                    </div>
                  )}
                </article>
              </div>
            </section>
          )}

            {performanceSummary && (
              <section className="admin-section" aria-label="Performance">
                <h2 className="admin-section__title">Performance</h2>
                <p className="admin-section__subtitle">Database, connections, errors.</p>
                <div className="admin-section__grid">
                  <article className="admin-usage-card" role="region">
                    <h3>Database</h3>
                    <div className="admin-usage-row">
                      <span className="admin-usage-label">Total size</span>
                      <span className="admin-usage-value">{performanceSummary.database.totalSizeFormatted}</span>
                    </div>
                    <div className="admin-usage-row">
                      <span className="admin-usage-label">Tables</span>
                      <span className="admin-usage-value">{performanceSummary.database.tableSizes.length}</span>
                    </div>
                    {performanceSummary.database.tableSizes.length > 0 && (
                      <div style={{ marginTop: 'var(--space-3)' }}>
                        <div className="admin-card__label">Top tables</div>
                        <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                          {performanceSummary.database.tableSizes.slice(0, 8).map((table) => (
                            <div key={table.tableName} className="admin-usage-row" style={{ fontSize: 'var(--font-size-sm)' }}>
                              <span className="admin-usage-label">{table.tableName}</span>
                              <span className="admin-usage-value">{table.sizeFormatted}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </article>
                  <article className="admin-usage-card" role="region">
                    <h3>Connection pool</h3>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Total connections
                      </div>
                      <div className="admin-usage-value">
                        {performanceSummary.connectionPool.totalConnections}
                      </div>
                    </div>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Active
                      </div>
                      <div className="admin-usage-value">
                        {performanceSummary.connectionPool.activeConnections}
                      </div>
                    </div>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Idle
                      </div>
                      <div className="admin-usage-value">
                        {performanceSummary.connectionPool.idleConnections}
                      </div>
                    </div>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Waiting
                      </div>
                      <div className="admin-usage-value">
                        {performanceSummary.connectionPool.waitingConnections}
                      </div>
                    </div>
                    <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
                      <div className="admin-card__label" style={{ fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-1)' }}>
                        Pool utilization
                      </div>
                      <div style={{ 
                        width: '100%', 
                        height: '8px', 
                        backgroundColor: 'var(--color-border)', 
                        borderRadius: 'var(--radius-sm)',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${performanceSummary.connectionPool.totalConnections > 0 
                            ? (performanceSummary.connectionPool.activeConnections / performanceSummary.connectionPool.totalConnections) * 100 
                            : 0}%`,
                          height: '100%',
                          backgroundColor: performanceSummary.connectionPool.activeConnections / (performanceSummary.connectionPool.totalConnections || 1) > 0.8 
                            ? '#ef4444' 
                            : performanceSummary.connectionPool.activeConnections / (performanceSummary.connectionPool.totalConnections || 1) > 0.6 
                            ? '#f59e0b' 
                            : '#10b981',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  </article>
                  <article className="admin-usage-card" role="region" aria-label="Error rates">
                    <h3>Error rates</h3>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Errors (24h)
                      </div>
                      <div className="admin-usage-value">
                        {performanceSummary.errorRates.errorsLast24Hours}
                      </div>
                    </div>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Errors (7d)
                      </div>
                      <div className="admin-usage-value">
                        {performanceSummary.errorRates.errorsLast7Days}
                      </div>
                    </div>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Errors (30d)
                      </div>
                      <div className="admin-usage-value">
                        {performanceSummary.errorRates.errorsLast30Days}
                      </div>
                    </div>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Error rate (24h)
                      </div>
                      <div className="admin-usage-value" style={{
                        color: performanceSummary.errorRates.errorRateLast24Hours > 5 
                          ? '#ef4444' 
                          : performanceSummary.errorRates.errorRateLast24Hours > 1 
                          ? '#f59e0b' 
                          : '#10b981'
                      }}>
                        {performanceSummary.errorRates.errorRateLast24Hours.toFixed(2)}%
                      </div>
                    </div>
                  </article>
                  <article className="admin-usage-card" role="region" aria-label="Query performance">
                    <h3>Query performance</h3>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Slow queries (24h)
                      </div>
                      <div className="admin-usage-value">
                        {performanceSummary.queryPerformance.slowQueriesLast24Hours}
                      </div>
                    </div>
                    <div className="admin-usage-row">
                      <div className="admin-usage-label">
                        Avg query time
                      </div>
                      <div className="admin-usage-value">
                        {performanceSummary.queryPerformance.avgQueryTime > 0 
                          ? `${performanceSummary.queryPerformance.avgQueryTime.toFixed(2)}ms`
                          : 'N/A'}
                      </div>
                    </div>
                  </article>
                </div>
              </section>
            )}
          </>
        ) : (
          <p className="admin-loading">No statistics available.</p>
        )}
    </div>
  );
};

export default AdminDashboard;
